import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { FailsInfo } from "../commonObj";
import {
  rejectCategoryCodes,
  rejectCategoryLabel,
} from "../pages/Templates/RejectCategoryCode";

/**
 * reject 대상 항목(fails). 기존에 고정으로 보내던 배열과 같은 순서를 유지한다.
 * 라벨은 commonObj 의 FailsInfo 를 그대로 쓴다.
 */
const FAIL_CODES = ["video", "image", "file", "description"];

type RejectDlgProps = {
  open: boolean;
  contentText?: string;
  handleNo: () => void;
  /** fails / categories 는 각각 최소 1개가 선택된 상태로만 호출된다. */
  handleRejectYes: (fails: string[], categories: string[]) => void;
};

/**
 * 체크박스 그룹 제목 + 한 줄 설명.
 * 설명 문구는 두 그룹의 축 차이(어디가 / 왜)를 드러내기 위한 것이다.
 */
const GroupHeading = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <Box sx={{ mb: "4px" }}>
    <Typography sx={{ fontWeight: 500 }}>{title}</Typography>
    <Typography sx={{ fontSize: "12px", color: "#79747E" }}>
      {description}
    </Typography>
  </Box>
);

/**
 * Reject 확인 다이얼로그.
 *
 * 공용 Dlg 와 분리한 이유: Dlg 는 publish / blind / reviewer 등 6개 다이얼로그가
 * 공유하는 컴포넌트라, reject 전용 체크박스를 넣으면 회귀 범위가 불필요하게 넓어진다.
 */
const RejectDlg = ({
  open,
  contentText = "Are you sure you want to reject selected projects?",
  handleNo,
  handleRejectYes,
}: RejectDlgProps) => {
  const [fails, setFails] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);

  // 닫았다 다시 열면 이전 선택이 남지 않도록 초기화한다.
  useEffect(() => {
    if (!open) {
      setFails([]);
      setCategories([]);
    }
  }, [open]);

  const toggle = (
    value: string,
    checked: boolean,
    list: string[],
    setList: (next: string[]) => void
  ) => {
    setList(checked ? [...list, value] : list.filter((v) => v !== value));
  };

  // 둘 다 최소 1개씩 선택해야 reject 할 수 있다.
  const canReject = fails.length > 0 && categories.length > 0;

  const onYes = () => {
    if (!canReject) return;
    handleRejectYes(fails, categories);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // 공용 Dlg 와 달리 y / Enter 즉시 제출은 두지 않는다. 항목을 골라야 하는
    // 화면이라 오발 위험이 크다. ESC(=No)만 지원한다.
    if (event.key === "Escape") {
      handleNo();
    }
  };

  return (
    <Dialog
      open={open}
      onKeyDown={handleKeyDown}
      // 사이드바 단축키 가드가 이 id 로 "다이얼로그 열림"을 판별한다. 빼면 다이얼로그를
      // 열어둔 채 t / m 을 눌렀을 때 페이지를 이탈한다.
      PaperProps={{ id: "openedDlg" }}
    >
      <DialogContent>
        <Box>
          <DialogContentText>{contentText}</DialogContentText>
        </Box>

        <Box sx={{ mt: "20px" }}>
          <GroupHeading
            title="Fails"
            description="Which part of the upload failed review."
          />
          <Box sx={{ display: "flex", flexWrap: "wrap" }}>
            {FAIL_CODES.map((code) => (
              <FormControlLabel
                key={code}
                sx={{ width: "45%" }}
                control={
                  <Checkbox
                    checked={fails.includes(code)}
                    onChange={(e) =>
                      toggle(code, e.target.checked, fails, setFails)
                    }
                  />
                }
                label={FailsInfo[code] ?? code}
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ mt: "16px" }}>
          <GroupHeading
            title="Reject Categories"
            description="Why the template is being rejected."
          />
          <Box sx={{ display: "flex", flexWrap: "wrap" }}>
            {rejectCategoryCodes.map((code) => (
              <FormControlLabel
                key={code}
                sx={{ width: "45%" }}
                control={
                  <Checkbox
                    checked={categories.includes(code)}
                    onChange={(e) =>
                      toggle(code, e.target.checked, categories, setCategories)
                    }
                  />
                }
                label={rejectCategoryLabel(code)}
              />
            ))}
          </Box>
        </Box>
      </DialogContent>

      <Box sx={{ mb: "24px", mr: "24px" }}>
        <DialogActions>
          <Button onClick={handleNo}>
            <b>
              <u>N</u>
            </b>
            O
          </Button>
          <Button onClick={onYes} disabled={!canReject}>
            <b>
              <u>Y</u>
            </b>
            ES
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default RejectDlg;
